/**
 * CinematicOverlaysLayer — the always-on cinema effects that sit on
 * top of every slide but BELOW the player chrome / progress bar / dialog.
 *
 * What it renders:
 *   - Always-on vignette (radial darkening at the edges)
 *   - Always-on animated film grain (SVG noise)
 *   - Per-slide-change light leak (pink by default, gold for FINAL)
 *
 * The legacy iris-cover / iris-reveal pulse was REMOVED here: with the
 * new page-transition stage (`transitions/PageTransitionStage`) every
 * transition renders its own iris cut when the user picks the
 * `iris-cut` row in the transition picker — there's no need for two
 * mechanisms firing simultaneously.
 *
 * Z-index range: 4..5
 *   - vignette        → 4
 *   - light-leak      → 4.5
 *   - film-grain      → 5
 */
import type { PreviewSlideData } from '../PreviewSlide';

interface Props {
  slide: PreviewSlideData;
  /** Bumped on every slide change so the light-leak replays. */
  playbackKey: number | string;
  /** Fires a gold light-leak instead of the default pink. */
  lightLeakGold?: boolean;
}

export function CinematicOverlaysLayer({
  slide,
  playbackKey,
  lightLeakGold,
}: Props) {
  return (
    <>
      <div className="preview-vignette" />
      <div
        key={`leak-${playbackKey}`}
        className={`preview-light-leak ${lightLeakGold ? 'gold' : ''}`}
      />
      <div className="preview-film-grain" />
      {/* Removed: preview-iris-cover / preview-iris-reveal. The page
         transition stage now owns the iris-cut when it's the picked
         transition (see transitions/page-transitions.css
         [data-t="iris"]). Leaving these off every slide keeps the
         preview fast and stops the always-on pulse from competing
         with the genuine transition animation. */}
    </>
  );
}

/** Suppress unused-param warning when callers pass slides with no
 *  cinematic overlays needed. The signature is intentionally stable
 *  (still takes `slide`) so refactors of the parent stage don't
 *  ripple here. */
export function _slide(_slide: PreviewSlideData) {
  return _slide;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ignoredSlideRef = _slide;
